import React, { useEffect, useRef, useState, memo, CSSProperties } from "react";

const isFirefox =
  typeof navigator !== "undefined" && /firefox/i.test(navigator.userAgent);

interface SmartVideoPlayerChatProps {
  videoUrl: string;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  preload?: "auto" | "metadata" | "none";
  autoPlayWhenVisible?: boolean;
  /** Never pause due to IO (use for lightbox). */
  noVisibilityPause?: boolean;
  /** If within N seconds of end, ignore IO pause. */
  suppressPauseNearEndSec?: number;
  /** Debounce IO-driven pause by N ms to avoid flicker in Firefox. */
  pauseDebounceMs?: number;
  /** IO root margin to keep “visible” longer. */
  visibilityRootMargin?: string;

  containerStyle?: CSSProperties;
  videoStyle?: CSSProperties;
  videoProps?: React.VideoHTMLAttributes<HTMLVideoElement> & Record<string, any>;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const SmartVideoPlayerChat: React.FC<SmartVideoPlayerChatProps> = ({
  videoUrl,
  muted = true,
  loop = false,
  controls = false,
  preload = "metadata",
  autoPlayWhenVisible = true,
  noVisibilityPause = false,
  suppressPauseNearEndSec = 0,
  pauseDebounceMs = 300,
  visibilityRootMargin = "600px 0px",
  containerStyle,
  videoStyle,
  videoProps,
  containerProps,
  onClick,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const nearEndRef = useRef(false);
  const pauseTimerRef = useRef<number | null>(null);

  // --- Firefox gapless loop hack state ---
  const preLoopJumpedRef = useRef(false); // prevent multiple jumps per cycle

  // Track near-end continuously (and reuse for IO guard)
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onTime = () => {
      if (!suppressPauseNearEndSec) { nearEndRef.current = false; return; }
      const d = v.duration;
      nearEndRef.current = Number.isFinite(d) && d > 0 && (d - v.currentTime) <= suppressPauseNearEndSec;
    };
    v.addEventListener("timeupdate", onTime);
    return () => v.removeEventListener("timeupdate", onTime);
  }, [suppressPauseNearEndSec]);

  // Intersection Observer
  useEffect(() => {
    if (!autoPlayWhenVisible) return;
    const node = containerRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => setIsVisible(entries[0]?.isIntersecting ?? false),
      { root: null, rootMargin: visibilityRootMargin, threshold: 0.01 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [autoPlayWhenVisible, visibilityRootMargin]);

  // Play/Pause with debounce + near-end guard
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !autoPlayWhenVisible) return;

    if (pauseTimerRef.current) {
      window.clearTimeout(pauseTimerRef.current);
      pauseTimerRef.current = null;
    }

    if (isVisible) {
      if (v.paused) v.play().catch(() => {});
    } else {
      if (noVisibilityPause || nearEndRef.current) return;
      pauseTimerRef.current = window.setTimeout(() => {
        if (!noVisibilityPause && !nearEndRef.current) v.pause();
        pauseTimerRef.current = null;
      }, pauseDebounceMs);
    }
  }, [isVisible, autoPlayWhenVisible, noVisibilityPause, pauseDebounceMs]);

  // Native loop for non-Firefox; manual loop for Firefox
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;

    // Reset the guard shortly after a successful loop
    const resetGuardIfAtStart = () => {
      if (v.currentTime < 0.1) preLoopJumpedRef.current = false;
    };

    // Manual loop: jump a bit before the end to avoid FF stall
    const onTimeupdateManualLoop = () => {
      if (!isFirefox || !loop) return;
      const d = v.duration;
      if (!Number.isFinite(d) || d <= 0) return;

      const remaining = d - v.currentTime;
      // Adjust threshold as needed (0.25–0.5s works well)
      if (remaining <= 0.1 && !preLoopJumpedRef.current) {
        preLoopJumpedRef.current = true;
        try {
          // Avoid exact 0 to dodge decoder edge-cases
          v.currentTime = 0.01;
          v.play().catch(() => {});
        } catch {}
      }
    };

    // Fallbacks: if FF emits waiting/stalled near end, force the jump
    const forceJumpIfNearEnd = () => {
      if (!isFirefox || !loop) return;
      const d = v.duration;
      if (!Number.isFinite(d) || d <= 0) return;
      const remaining = d - v.currentTime;
      if (remaining <= 1.0) {
        preLoopJumpedRef.current = true;
        try {
          v.currentTime = 0.01;
          v.play().catch(() => {});
        } catch {}
      }
    };

    const onEndedNative = () => {
      if (isFirefox && loop) {
        // Should be rare if timeupdate handled it; still ensure continuity
        preLoopJumpedRef.current = true;
        try {
          v.currentTime = 0.01;
          v.play().catch(() => {});
        } catch {}
      }
    };

    v.addEventListener("timeupdate", onTimeupdateManualLoop);
    v.addEventListener("waiting", forceJumpIfNearEnd);
    v.addEventListener("stalled", forceJumpIfNearEnd);
    v.addEventListener("ended", onEndedNative);
    v.addEventListener("seeked", resetGuardIfAtStart);

    return () => {
      v.removeEventListener("timeupdate", onTimeupdateManualLoop);
      v.removeEventListener("waiting", forceJumpIfNearEnd);
      v.removeEventListener("stalled", forceJumpIfNearEnd);
      v.removeEventListener("ended", onEndedNative);
      v.removeEventListener("seeked", resetGuardIfAtStart);
    };
  }, [loop]);

  // Force fuller buffer on Firefox (helps reduce end-of-stream hiccup)
  const effectivePreload = isFirefox ? "auto" : preload;

  return (
    <div
      ref={containerRef}
      {...containerProps}
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#000",
        overflow: "hidden",
        borderRadius: 12,
        ...containerStyle,
      }}
    >
      <video
        ref={videoRef}
        src={videoUrl}
        muted={muted}
        // IMPORTANT: disable native loop on Firefox to avoid the 1s hang
        loop={isFirefox ? false : loop}
        controls={controls}
        playsInline
        preload={effectivePreload}
        // Keep your existing videoProps (e.g., autoPlay) for non-Firefox
        {...videoProps}
        style={{
          maxWidth: "100%",
          maxHeight: "100%",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          display: "block",
          backgroundColor: "#000",
          ...videoStyle,
        }}
      />
    </div>
  );
};

export default memo(SmartVideoPlayerChat);
